import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Snackbar,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { ContentCopy, Email, Paid, Person, PhoneIphone, Refresh, SportsEsports, WorkspacePremium } from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { usePalworldPerfil } from '../contexts/PalworldPerfilContext'
import { formatBrazilDateFromBackend } from '../utils/dateTime'

const formatWhatsapp = (value: string | undefined) => {
  const digits = String(value ?? '').replace(/\D/g, '')
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  return value || '—'
}

const formatBRL = (value: number | null | undefined) => {
  const v = Number(value ?? 0)
  if (Number.isNaN(v)) return '—'
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const initialFrom = (text: string | undefined | null) => {
  const t = String(text ?? '').trim()
  if (!t) return '?'
  return t[0]!.toUpperCase()
}

const DetailRow: React.FC<{
  icon: React.ReactNode
  label: string
  value: string
  onCopy?: () => void
}> = ({ icon, label, value, onCopy }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25 }}>
    <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,0.06)' }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 900, wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
    {onCopy && (
      <Tooltip title="Copiar">
        <IconButton onClick={onCopy} size="small" sx={{ border: '1px solid rgba(255,255,255,0.10)' }}>
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Box>
)

const MeuPerfilPage: React.FC = () => {
  const { user } = useAuth()
  const { perfil: data, isLoading: loading, error, refresh } = usePalworldPerfil()
  const [snack, setSnack] = useState<string | null>(null)

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setSnack('Copiado!')
    } catch {
      setSnack('Não foi possível copiar.')
    }
  }

  useEffect(() => {
    void refresh()
  }, [refresh])

  const email = data?.email || user?.username || '—'
  const whatsappDigits = String(data?.whatsappNumber ?? '').replace(/\D/g, '')
  const whatsappFormatted = formatWhatsapp(data?.whatsappNumber)
  const playerName = data?.palPlayerName || '—'
  const totalGasto = formatBRL(data?.totalGastoPalworld)

  const chips = useMemo(() => {
    const c: Array<{ label: string; icon: React.ReactElement }> = []
    if (data?.palPlayerName) c.push({ label: `Player: ${data.palPlayerName}`, icon: <SportsEsports sx={{ fontSize: 16 }} /> })
    if (data?.whatsappNumber) c.push({ label: `WhatsApp: ${formatWhatsapp(data.whatsappNumber)}`, icon: <PhoneIphone sx={{ fontSize: 16 }} /> })
    if ((data?.vipAtual?.diasRestantes ?? 0) > 0) {
      c.push({
        label: `VIP ativo • ${Number(data?.vipAtual?.diasRestantes ?? 0).toLocaleString('pt-BR')}d`,
        icon: <WorkspacePremium sx={{ fontSize: 16 }} />
      })
    }
    return c
  }, [data?.palPlayerName, data?.vipAtual?.diasRestantes, data?.whatsappNumber])

  const vip = data?.vipAtual ?? null
  const vipActive = (vip?.diasRestantes ?? 0) > 0
  const vipProgress = (() => {
    const diasVip = Number(vip?.diasVip ?? 0) || 0
    const restantes = Number(vip?.diasRestantes ?? 0) || 0
    if (!diasVip) return 0
    const used = Math.max(0, diasVip - Math.max(0, restantes))
    return Math.max(0, Math.min(100, Math.round((used / diasVip) * 100)))
  })()

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person />
              Meu Perfil
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>Dados da sua conta e do Palworld.</Typography>
          </Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => void refresh()} disabled={loading}>
            Atualizar
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {loading && !data ? (
          <Box sx={{ minHeight: '35dvh', display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Card sx={{ overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.25} alignItems={{ sm: 'center' }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        fontWeight: 900,
                        bgcolor: 'rgba(0,230,118,0.18)',
                        border: '1px solid rgba(0,230,118,0.30)',
                        color: 'text.primary'
                      }}
                    >
                      {initialFrom(data?.palPlayerName || email)}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.1 }}>
                        Conta
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.15, wordBreak: 'break-word' }}>
                        {email}
                      </Typography>

                      {chips.length > 0 && (
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                          {chips.map((c) => (
                            <Chip
                              key={c.label}
                              icon={c.icon}
                              label={c.label}
                              variant="outlined"
                              sx={{ borderColor: 'rgba(255,255,255,0.16)', fontWeight: 800 }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Paid sx={{ color: 'primary.main' }} />
                      <Typography sx={{ fontWeight: 900, fontSize: 18 }}>{totalGasto}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.1 }}>
                    Total gasto
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 900, mt: 0.25 }}>
                    {totalGasto}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
                    Valor total em R$ (Palworld).
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card
                sx={{
                  overflow: 'hidden',
                  borderColor: vipActive ? 'rgba(0,230,118,0.25)' : 'rgba(255,255,255,0.10)',
                  backgroundImage: vipActive
                    ? 'radial-gradient(900px 250px at 10% 0%, rgba(0,230,118,0.18), transparent 55%),' +
                      'radial-gradient(900px 250px at 90% 100%, rgba(0,217,255,0.14), transparent 55%),' +
                      'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 70%)'
                    : undefined
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <WorkspacePremium sx={{ color: vipActive ? 'primary.main' : 'text.secondary' }} />
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>
                          VIP
                        </Typography>
                        {vipActive ? (
                          <Chip
                            size="small"
                            label="Ativo"
                            variant="outlined"
                            sx={{ fontWeight: 900, borderColor: 'rgba(0,230,118,0.35)' }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label="Inativo"
                            variant="outlined"
                            sx={{ fontWeight: 900, borderColor: 'rgba(255,255,255,0.18)' }}
                          />
                        )}
                      </Stack>

                      <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {vipActive
                          ? `Você tem ${Number(vip?.diasRestantes ?? 0).toLocaleString('pt-BR')} dia(s) restantes.`
                          : 'Você não possui VIP ativo no momento.'}
                      </Typography>

                      {vip ? (
                        <Box sx={{ mt: 2 }}>
                          <LinearProgress
                            variant="determinate"
                            value={vipProgress}
                            sx={{
                              height: 10,
                              borderRadius: 999,
                              bgcolor: 'rgba(255,255,255,0.08)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 999,
                                background: vipActive
                                  ? 'linear-gradient(90deg, rgba(0,230,118,1) 0%, rgba(0,217,255,1) 100%)'
                                  : 'linear-gradient(90deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.18) 100%)'
                              }
                            }}
                          />
                          <Stack direction="row" spacing={2} sx={{ mt: 1.25 }} useFlexGap flexWrap="wrap">
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Início: <strong>{formatBrazilDateFromBackend(vip.dataInicio)}</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Fim: <strong>{formatBrazilDateFromBackend(vip.dataFim)}</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Dias: <strong>{Number(vip.diasVip ?? 0).toLocaleString('pt-BR')}</strong>
                            </Typography>
                          </Stack>
                        </Box>
                      ) : null}
                    </Box>

                    <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.1 }}>
                        Dias restantes
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1 }}>
                        {vipActive ? Number(vip?.diasRestantes ?? 0).toLocaleString('pt-BR') : '—'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {vipActive ? 'Aproveite seus benefícios.' : 'Compre um pacote VIP para ativar.'}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Contato
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Informações para comunicação.
                  </Typography>
                  <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.08)' }} />

                  <DetailRow
                    icon={<Email fontSize="small" />}
                    label="Email"
                    value={data?.email || '—'}
                    onCopy={data?.email ? () => void copyToClipboard(data.email) : undefined}
                  />
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                  <DetailRow
                    icon={<PhoneIphone fontSize="small" />}
                    label="WhatsApp"
                    value={whatsappFormatted}
                    onCopy={whatsappDigits ? () => void copyToClipboard(whatsappDigits) : undefined}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Palworld
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Identificação e compras.
                  </Typography>
                  <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.08)' }} />

                  <DetailRow
                    icon={<SportsEsports fontSize="small" />}
                    label="Player name"
                    value={playerName}
                    onCopy={data?.palPlayerName ? () => void copyToClipboard(data.palPlayerName) : undefined}
                  />
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                  <DetailRow icon={<Paid fontSize="small" />} label="Total gasto" value={totalGasto} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Stack>

      <Snackbar
        open={!!snack}
        autoHideDuration={1500}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  )
}

export default MeuPerfilPage
